'use client'

import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { cn } from "@/lib/utils";

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface FaqProps {
  readonly title?: string;
  readonly items: FaqItem[];
  readonly className?: string;
  readonly itemClassName?: string;
  readonly collapsible?: boolean;
  readonly defaultExpanded?: boolean;
}

export function Faq({
  title = "Frequently Asked Questions",
  items,
  className,
  itemClassName,
  collapsible = true,
  defaultExpanded = false
}: FaqProps) {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    defaultExpanded ? new Set(items.map(item => item.id)) : new Set()
  );

  const toggleItem = (id: string) => {
    if (!collapsible) return;
    
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  return (
    <div className={cn("mb-12", className)}>
      {title && (
        <h2 className="text-2xl font-bold text-neutral-emphasis mb-8 text-center">
          {translate(title)}
        </h2>
      )}
      <div className="space-y-6">
        {items.map((item) => {
          const isExpanded = !collapsible || expandedItems.has(item.id);
          
          return (
            <div
              key={item.id}
              className={cn(
                "bg-card p-6 rounded-lg shadow-md transition-all duration-200",
                collapsible && "cursor-pointer hover:shadow-lg",
                itemClassName
              )}
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-neutral-emphasis flex-1 pr-4">
                  {translate(item.question)}
                </h3>
                {collapsible && (
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-neutral-muted" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-neutral-muted" />
                    )}
                  </div>
                )}
              </div>

              <div className={cn(
                "overflow-hidden transition-all duration-300",
                isExpanded ? "max-h-none opacity-100 mt-4" : "max-h-0 opacity-0"
              )}>
                <p className="text-neutral-muted leading-relaxed">
                  {translate(item.answer)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Alternative simpler version for always-expanded FAQs
export function SimpleFaq({
  title = "Frequently Asked Questions",
  items,
  className,
  itemClassName
}: Omit<FaqProps, 'collapsible' | 'defaultExpanded'>) {
  return (
    <Faq
      title={title}
      items={items}
      className={className}
      itemClassName={itemClassName}
      collapsible={false}
    />
  );
}

export default Faq;
